import React, { useState, useRef, useEffect } from 'react';
import { RevoltInput, RevoltTextarea, RevoltSwitch } from '../../../../../components';
import { ProductCard } from '../../../components';
import { ProductRefSearchAdd } from './ProductRefSearchAdd';
import { parseMarkdown, setupCodeBlockCopyButtons, setupExternalLinkHandlers, renderMath } from '../../../../../core';
import './ProductDetailsView.css';
import { RevoltDesktopAutoUpdaterDownloadButton } from './RevoltDesktopAutoUpdaterDownloadButton';

export function ProductDetailsView({ product, admin = false, canViewRevoltDesktopDownloads = false, manager, onOpenTagsModal, onOpenAccessTagsModal, onOpenImageModal, onOpenDownloadsModal, onFeaturedChange, onActiveChange, onBundleChange, onDeleteClick, onProductClick, onAddBundle, onRemoveBundle, onAddRelated, onRemoveRelated, onBuyClick, buyLoading = false }) {
  // Admin editing state - always call hooks first
  const [editingField, setEditingField] = useState(null); // 'title', 'price', 'description', or null
  const [descriptionEditMinHeight, setDescriptionEditMinHeight] = useState(null);
  const descriptionMarkdownRef = useRef(null);
  const descriptionDisplayWrapRef = useRef(null);

  const [editValues, setEditValues] = useState({
    title: '',
    price: '',
    description: ''
  });

  // Update edit values when product changes
  React.useEffect(() => {
    if (product) {
      setEditValues({
        title: product.name || '',
        price: product.price !== undefined ? product.price.toString() : '',
        description: product.description || ''
      });
    }
  }, [product]);

  // Render markdown in description when displaying (not editing)
  useEffect(() => {
    if (editingField === 'description' || !descriptionMarkdownRef.current) return;

    const content = editValues.description?.trim() || '';
    if (!content) {
      descriptionMarkdownRef.current.innerHTML = '';
      return;
    }

    const parsedHtml = parseMarkdown(content);
    descriptionMarkdownRef.current.innerHTML = parsedHtml;
    setupCodeBlockCopyButtons(descriptionMarkdownRef.current);
    setupExternalLinkHandlers(descriptionMarkdownRef.current);
    renderMath(descriptionMarkdownRef.current);
  }, [editValues.description, editingField]);

  if (!product) return null;

  // Gate:
  // - `admin` toggles edit UI.
  // - `canViewRevoltDesktopDownloads` is true on public details; admin always sees downloads via `admin`.
  const canShowRevoltDesktopDownloads = Boolean(admin) || Boolean(canViewRevoltDesktopDownloads);

  // Admin editing handlers
  const startEditing = (field) => {
    if (admin) {
      if (field === 'description' && descriptionDisplayWrapRef.current) {
        const h = descriptionDisplayWrapRef.current.offsetHeight;
        setDescriptionEditMinHeight(h ? h + 16 : null); // match display + small padding
      }
      setEditingField(field);
    }
  };

  const cancelEditing = () => {
    setEditingField(null);
    setDescriptionEditMinHeight(null);
    // Reset values to original product data
    setEditValues({
      title: product.name || '',
      price: product.price !== undefined ? product.price.toString() : '',
      description: product.description || ''
    });
  };

  const saveEditing = async (field) => {
    try {
      const value = editValues[field];
      const currentName = (product.name || '').trim();
      const currentDescription = (product.description || '').trim();
      const currentPrice = parseFloat(product.price ?? 0);
      let nextName = currentName;
      let nextDescription = currentDescription;
      let nextPrice = currentPrice;

      if (field === 'price') {
        const numValue = parseFloat(value);
        if (isNaN(numValue) || numValue < 0) {
          alert('Please enter a valid price');
          return;
        }
        nextPrice = numValue;
      } else if (field === 'title') {
        const trimmed = value.trim();
        if (!trimmed) {
          alert('Product name is required');
          return;
        }
        nextName = trimmed;
      } else if (field === 'description') {
        const trimmed = value.trim();
        if (!trimmed) {
          alert('Product description is required');
          return;
        }
        nextDescription = trimmed;
      }

      // Backend PUT applies $set for every field included here — omitting `downloads` used to clear it server-side.
      const updateData = {
        name: nextName,
        description: nextDescription,
        price: nextPrice,
        tags: Array.isArray(product.tags) ? product.tags : [],
        accountTags: Array.isArray(product.accountTags) ? product.accountTags : [],
        thumbnail: typeof product.thumbnail === 'string' ? product.thumbnail : null,
        images: Array.isArray(product.images) ? product.images : [],
        downloads: Array.isArray(product.downloads) ? product.downloads : [],
        isFeatured: Boolean(product.isFeatured),
        isActive: product.isActive !== false,
        isBundle: Boolean(product.isBundle),
        bundleIds: Array.isArray(product.bundleIds) ? product.bundleIds : [],
        relatedProductIds: Array.isArray(product.relatedProductIds) ? product.relatedProductIds : []
      };

      const productId = product.id || product._id;
      const updatedProduct = await manager.updateProduct(productId, updateData);

      // Sync edit state from canonical returned record so UI updates immediately.
      if (updatedProduct) {
        setEditValues({
          title: updatedProduct.name || updateData.name,
          price: String(
            updatedProduct.price !== undefined && updatedProduct.price !== null
              ? updatedProduct.price
              : updateData.price
          ),
          description: updatedProduct.description || updateData.description
        });
      } else {
        setEditValues((prev) => ({
          ...prev,
          title: updateData.name,
          price: String(updateData.price),
          description: updateData.description
        }));
      }

      // Update the product data to reflect changes in UI
      // Note: In a full implementation, you might want to refetch or update the product state

      setEditingField(null);
      setDescriptionEditMinHeight(null);
    } catch (error) {
      console.error('Failed to save:', error);
      alert('Failed to save changes');
    }
  };

  const handleInputChange = (field, value) => {
    setEditValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleKeyDown = (field, e) => {
    if (e.key === 'Enter' && field !== 'description') {
      saveEditing(field);
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  };


  const bundleProducts = product.bundleProducts || [];
  const relatedProducts = product.relatedProducts || [];
  // In admin mode, always show both sections so user can add products; otherwise only when there are active products to display
  // (backend filters out inactive refs, so bundleProducts/relatedProducts are empty when all linked products are inactive)
  const hasBundles = admin || bundleProducts.length > 0;
  const hasRelated = admin || relatedProducts.length > 0;

  return (
    <div className="product-details-view product-details-view--with-sections">
      <div className="product-details-main-row">
      <div className="product-details-left">
        {/* Header Section */}
        <div className="product-header-section">
          {/* Row 1: Product Title */}
          <div className="product-header-title-row">
            {editingField === 'title' ? (
              <div className="edit-input-container">
                <RevoltInput
                  type="text"
                  value={editValues.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  onBlur={() => saveEditing('title')}
                  onKeyDown={(e) => handleKeyDown('title', e)}
                  autoFocus
                  className="edit-input"
                />
              </div>
            ) : (
              <h1
                className={`product-title ${admin ? 'editable' : ''}`}
                onClick={() => startEditing('title')}
                title={admin ? 'Click to edit title' : undefined}
              >
                {editValues.title || 'Unnamed Product'}
                {product.isBundle && (
                  <span className="product-details-bundle-badge" title="Product bundle">
                    <i className="fa-solid fa-layer-group"></i>
                    Bundle
                  </span>
                )}
              </h1>
            )}
          </div>

          {/* Header internal separator (title -> price/tags) */}
          <div className="details-separator details-separator--horizontal"></div>

          {/* Row 2: Price + Tags */}
          <div className="product-header-meta-row">
            <div className="product-price-section">
              {editingField === 'price' ? (
                <div className="edit-input-container">
                  <RevoltInput
                    type="number"
                    step="0.01"
                    min="0"
                    value={editValues.price}
                    onChange={(e) => handleInputChange('price', e.target.value)}
                    onBlur={() => saveEditing('price')}
                    onKeyDown={(e) => handleKeyDown('price', e)}
                    autoFocus
                    className="edit-input"
                    placeholder="0.00"
                  />
                </div>
              ) : (
                <span
                  className={`product-price ${admin ? 'editable' : ''}`}
                  onClick={() => startEditing('price')}
                  title={admin ? 'Click to edit price' : undefined}
                >
                  {parseFloat(editValues.price) === 0 ? 'Free' : `$${editValues.price}`}
                </span>
              )}
            </div>
            <div className="product-tags-section">
              {product.tags && product.tags.length > 0 && (
                <div className="product-tags">
                  {product.tags.map((tag, index) => (
                    <span key={index} className="product-tag">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
              {admin && (
                <div className="edit-tags-buttons-row">
                  <button
                    className="edit-tags-button"
                    onClick={() => onOpenTagsModal && onOpenTagsModal()}
                    title="Edit tags"
                  >
                    <i className="fa-solid fa-edit"></i>
                    Edit Tags
                  </button>
                  <button
                    className="edit-tags-button"
                    onClick={() => onOpenAccessTagsModal && onOpenAccessTagsModal()}
                    title="Edit access tags"
                  >
                    <i className="fa-solid fa-key"></i>
                    Edit Access Tags
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Header -> Information separator */}
        <div className="details-separator details-separator--horizontal"></div>

        {/* Information Section */}
        <div className="product-info-section">
          <div
            className={`product-description-edit-wrap ${editingField === 'description' ? 'is-active' : ''}`}
          >
            <div className="edit-input-container">
              <RevoltTextarea
                value={editValues.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                onBlur={() => saveEditing('description')}
                onKeyDown={(e) => handleKeyDown('description', e)}
                autoFocus
                className="edit-textarea"
                placeholder="Enter product description..."
                rows={4}
                minRows={1}
                maxRows={9999}
                style={descriptionEditMinHeight ? { minHeight: `${descriptionEditMinHeight}px` } : undefined}
              />
            </div>
          </div>
          <div
            ref={descriptionDisplayWrapRef}
            className={`product-description-display-wrap ${editingField !== 'description' ? 'is-active' : ''}`}
          >
            {editValues.description?.trim() ? (
              <div
                ref={descriptionMarkdownRef}
                className={`product-description product-description-markdown ${admin ? 'editable' : ''}`}
                onClick={() => admin && startEditing('description')}
                title={admin ? 'Click to edit description' : undefined}
              />
            ) : (
              <p
                className={`product-description product-description--empty ${admin ? 'editable' : ''}`}
                onClick={() => admin && startEditing('description')}
                title={admin ? 'Click to edit description' : undefined}
              >
                No description available.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Root vertical separator between left and right sections */}
      <div className="details-separator details-separator--vertical"></div>

      <div className="product-details-right">
        {/* Right section - Purchase area */}
        <div className="purchase-section">
          {!admin && (
            <div className="buy-button-wrap">
              {product?.hasPurchased && product?.stripe?.productId ? (
                <div className="product-downloads-cluster">
                  <div className="product-downloads-label">
                    <i className="fa-solid fa-check-circle"></i>
                    Purchased
                  </div>
                  {Array.isArray(product?.downloads) && product.downloads.length > 0 ? (
                    <div className="product-downloads-list">
                      {product.downloads.map((d, index) => {
                        if (!canShowRevoltDesktopDownloads && d?.mode === 'revolt-desktop') {
                          return null;
                        }

                        if (d?.mode === 'revolt-desktop') {
                          return (
                            <RevoltDesktopAutoUpdaterDownloadButton
                              key={index}
                              download={d}
                            />
                          );
                        }

                        return (
                          <a
                            key={index}
                            href={d.url}
                            download={d.fileName || d.name || undefined}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="product-download-item"
                          >
                            <i className="fa-solid fa-download"></i>
                            <span className="product-download-name">{d.name}</span>
                            {d.fileSize && (
                              <span className="product-download-size">{d.fileSize}</span>
                            )}
                          </a>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="product-downloads-empty">No downloads available</p>
                  )}
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    className="buy-button"
                    onClick={() => onBuyClick && onBuyClick(product)}
                    disabled={!onBuyClick || !product?.stripe?.productId || !product?.stripe?.priceId || buyLoading}
                    title={!product?.stripe?.productId ? 'Product is not available for purchase yet' : !onBuyClick ? 'Sign in to purchase' : undefined}
                  >
                    <i className="fa-solid fa-shopping-cart"></i>
                    Buy Now
                  </button>
                  {product?.stripe?.productId && product?.stripe?.priceId && !onBuyClick && (
                    <p className="buy-sign-in-hint">Sign in to purchase</p>
                  )}
                </>
              )}
            </div>
          )}
          {admin && onOpenDownloadsModal && (
            <button
              type="button"
              className="edit-downloads-button"
              onClick={onOpenDownloadsModal}
              title="Edit downloadable content"
            >
              <i className="fa-solid fa-download"></i>
              Edit Downloads
            </button>
          )}
          {admin && onFeaturedChange && (
            <div className="featured-switch-container">
              <span className="featured-label">Featured Product</span>
              <RevoltSwitch
                checked={Boolean(product.isFeatured)}
                onChange={onFeaturedChange}
                className="featured-switch"
              />
            </div>
          )}
          {admin && onActiveChange && (
            <div className="featured-switch-container">
              <span className="featured-label">Active</span>
              <RevoltSwitch
                checked={product.isActive !== false}
                onChange={onActiveChange}
                className="featured-switch"
              />
            </div>
          )}
          {admin && onBundleChange && (
            <div className="featured-switch-container">
              <span className="featured-label">Product Bundle</span>
              <RevoltSwitch
                checked={Boolean(product.isBundle)}
                onChange={onBundleChange}
                className="featured-switch"
              />
            </div>
          )}
          {admin && onDeleteClick && (
            <button
              type="button"
              className="product-delete-button"
              onClick={onDeleteClick}
              title="Delete product"
            >
              <i className="fa-solid fa-trash"></i>
              Delete
            </button>
          )}
        </div>
      </div>
      </div>

      {/* Bundles section - only show when has data */}
      {hasBundles && (
        <>
          <div className="details-separator details-separator--horizontal"></div>
          <div className="product-details-ref-section product-details-bundles-section">
          <div className="product-details-ref-header">
            <h3 className="product-details-ref-title">Part of these bundles</h3>
            {admin && onAddBundle && manager && (
              <ProductRefSearchAdd
                manager={manager}
                onSelect={onAddBundle}
                placeholder="Search bundles to add..."
                excludeIds={Array.isArray(product.bundleIds) ? product.bundleIds : []}
                excludeCurrentId={product.id}
              />
            )}
          </div>
          <div className="product-details-ref-grid">
            {bundleProducts.length > 0 ? (
              <div className="product-grid">
                  {bundleProducts.map((p) => (
                    <div key={p.id} className="product-details-ref-card">
                      <ProductCard product={p} onProductClick={onProductClick} admin={admin} />
                      {admin && onRemoveBundle && (
                        <button
                          type="button"
                          className="product-details-ref-remove"
                          onClick={() => onRemoveBundle(p)}
                          title="Remove from bundles"
                        >
                          <i className="fa-solid fa-times"></i>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
            ) : admin && product.bundleIds?.length > 0 ? (
              <div className="product-details-ref-loading">Loading...</div>
            ) : (
              <div className="product-details-ref-empty">No bundles linked</div>
            )}
          </div>
        </div>
        </>
      )}

      {/* Related products section - only show when has data */}
      {hasRelated && (
        <>
          <div className="details-separator details-separator--horizontal"></div>
          <div className="product-details-ref-section product-details-related-section">
          <div className="product-details-ref-header">
            <h3 className="product-details-ref-title">Related products</h3>
            {admin && onAddRelated && manager && (
              <ProductRefSearchAdd
                manager={manager}
                onSelect={onAddRelated}
                placeholder="Search products to add..."
                excludeIds={Array.isArray(product.relatedProductIds) ? product.relatedProductIds : []}
                excludeCurrentId={product.id}
              />
            )}
          </div>
          <div className="product-details-ref-grid">
            {relatedProducts.length > 0 ? (
              <div className="product-grid">
                {relatedProducts.map((p) => (
                  <div key={p.id} className="product-details-ref-card">
                    <ProductCard product={p} onProductClick={onProductClick} admin={admin} />
                    {admin && onRemoveRelated && (
                      <button
                        type="button"
                        className="product-details-ref-remove"
                        onClick={() => onRemoveRelated(p)}
                        title="Remove from related"
                      >
                        <i className="fa-solid fa-times"></i>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : admin && product.relatedProductIds?.length > 0 ? (
              <div className="product-details-ref-loading">Loading...</div>
            ) : (
              <div className="product-details-ref-empty">No related products</div>
            )}
          </div>
        </div>
        </>
      )}
    </div>
  );
}